import React, { useContext, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import EncryptContext from './EncryptContext';
import { getEncryptActionHandler, getEncryptButtonProps, getAjaxHandler } from 'Secure/Utils';
import {
  fpeFieldsListFromInventory,
  inventoryContentOf,
  latestInventoryOf,
  listDataSourceInventories,
} from 'Secure/api';
import AppContext from 'Utils/AppContext';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import { useHistory } from 'react-router-dom';
import { isFileDatasourceType } from 'DataSources/datasourcesApi';

function EncryptButton({ connection, datasourceType, dataSourceId, status, errorMessage, fieldQty, prevSearchid, hasMaskingDriver = true, readOnly = false }) {
  const [fetchingData, setFetchingData] = useState(false);
  // Decrypt inventory choice: a source can have several named inventories, so when more than one
  // carries FPE columns the user picks which one to decrypt. null = closed; array = modal open.
  const [decryptChoices, setDecryptChoices] = useState(null);
  const [decryptInventoryId, setDecryptInventoryId] = useState(null);
  const { setNotification, isReadOnly } = useContext(AppContext);
  // Encrypt only opens the inventory (reviewable); on an ENCRYPTED source the button starts a
  // decrypt job directly, which a read-only account cannot do.
  const decryptBlocked = !!isReadOnly && status === 'ENCRYPTED';
  const { fetchDataHandler } = useContext(EncryptContext);
  const history = useHistory();

  const buttonProps = readOnly
    ? { icon: null, text: 'READ ONLY', disabled: true }
    : getEncryptButtonProps(status, fetchingData, fieldQty, hasMaskingDriver);

  const encryptAction = getEncryptActionHandler(status);
  const encryptActionHandler = getAjaxHandler(setFetchingData, () => {}, encryptAction, fetchDataHandler);
  const action = status === 'ENCRYPTED' ? 'decryption' : 'encryption';

  const getTooltipText = (status) => {
    if (readOnly) {
      return 'This data source is read only.';
    }
    if (!hasMaskingDriver) {
      return isFileDatasourceType(datasourceType)
        ? 'Encryption is not available for this file format on the configured encrypt service.'
        : 'No JDBC driver with masking support is installed for this database type. Add a driver on the JDBC Drivers page.';
    }
    return status === 'ENCRYPTED'
      ? 'Decrypt using saved inventory (same key/tweak as encrypt).'
      : 'WARNING: Will run in-place encryption. Will encrypt all fields selected in inventory. All applications using this database will be negatively affected.';
  };

  const startDecrypt = async (inventoryId) => {
    await encryptActionHandler(dataSourceId, inventoryId);
    setNotification({
      header: `Running ${action}`,
      body: `Successfully started ${action} job for ${connection}`,
    });
  };

  const EncryptAction = () => {
    return (
      <Button
        className='w-100'
        onClick={async () => {
          if (status !== 'ENCRYPTED') {
            if (dataSourceId == null) {
              setNotification({
                header: 'Cannot open encryption inventory',
                body: 'Missing data source id',
              });
              return;
            }
            history.push(`/secure/encrypt/inventory/${encodeURIComponent(dataSourceId)}`, {
              prevSearchid,
            });
            return;
          }

          if (dataSourceId == null) {
            setNotification({
              header: 'Decrypt failed',
              body: 'Missing data source id',
            });
            return;
          }

          // Decrypt must run against the inventory that encrypted. One FPE inventory → start
          // right away; several → let the user pick which one to decrypt.
          let candidates = [];
          try {
            const inventories = await listDataSourceInventories(dataSourceId);
            const withFpe = inventories.filter(
              (inv) => fpeFieldsListFromInventory(inventoryContentOf(inv)).length > 0
            );
            candidates = withFpe.length > 0 ? withFpe : inventories;
          } catch (e) {
            candidates = [];
          }
          if (candidates.length === 0) {
            setNotification({
              header: 'Decrypt failed',
              body: 'No saved inventory for this data source',
            });
            return;
          }
          if (candidates.length === 1) {
            await startDecrypt(candidates[0].id);
            return;
          }
          const latest = latestInventoryOf(candidates);
          setDecryptInventoryId(latest ? latest.id : candidates[0].id);
          setDecryptChoices(candidates);
        }}
        variant={buttonProps.variant || 'outline-secondary'}
        size='sm'
        disabled={buttonProps.disabled || decryptBlocked}
        title={decryptBlocked ? READ_ONLY_TITLE : undefined}
      >
        {buttonProps.icon}
        {buttonProps.text}
      </Button>
    );
  };

  const decryptPicker = (
    <Modal show={decryptChoices != null} onHide={() => setDecryptChoices(null)} centered>
      <Modal.Header closeButton>
        <Modal.Title as='h6'>Decrypt {connection}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted small'>
          This data source has multiple saved inventories. Choose which one to decrypt — it should
          be the inventory the encryption job ran with.
        </p>
        <Form.Group className='mb-0'>
          <Form.Label>Inventory</Form.Label>
          <Form.Control
            as='select'
            data-testid='decrypt-inventory-select'
            value={decryptInventoryId != null ? String(decryptInventoryId) : ''}
            onChange={(e) => setDecryptInventoryId(Number(e.target.value))}
          >
            {(decryptChoices || []).map((inv) => (
              <option key={inv.id} value={String(inv.id)}>
                {inv.name || `Inventory ${inv.id}`}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={() => setDecryptChoices(null)}>
          Cancel
        </Button>
        <Button
          variant='primary'
          disabled={decryptInventoryId == null}
          onClick={async () => {
            const inventoryId = decryptInventoryId;
            setDecryptChoices(null);
            await startDecrypt(inventoryId);
          }}
        >
          Decrypt
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const render = errorMessage ? (
    <DiscoveryTooltip
      text={'The last encryption job failed. No data was changed — you can retry.\n\n' + errorMessage}
      placement='top'
    >
      {EncryptAction}
    </DiscoveryTooltip>
  ) : (
    <DiscoveryTooltip text={getTooltipText(status)} placement='top'>
      {EncryptAction}
    </DiscoveryTooltip>
  );

  return (
    <>
      {render}
      {decryptPicker}
    </>
  );
}

export default EncryptButton;
